import React, { useEffect, useState } from "react";
import { useHistory, useParams, Link } from "react-router-dom";
import { Button, Card, Alert, Form, Badge } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faArrowLeft, faSave, faTimes, faPlus } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";
import { CoTeacherCard, EDIT_PAGE } from "./CoTeacherCard";

const PageContainer = styled.div`
  max-width: 700px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 24px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const StyledCard = styled(Card)`
  border: 1px solid ${colors.milkGlass400};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const CardHeader = styled.div`
  background: ${colors.milkGlass200};
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.milkGlass400};
  font-weight: 600;
  color: ${colors.nightlyWoods900};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const RoleBadge = styled(Badge)`
  margin-right: 8px;
  margin-bottom: 8px;
  font-size: 0.9em;
  padding: 8px 12px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const RolesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 16px;
`;

const AddRoleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EDUCATOR_TYPE_OPTIONS = [
  { value: "", label: "None" },
  { value: "teacher", label: "Teacher" },
  { value: "math_specialist", label: "Math Specialist" },
  { value: "school_administrator", label: "School Administrator" },
  { value: "district_administrator", label: "District Administrator" },
];

const ROLE_OPTIONS = [
  { value: "superuser", label: "Superuser", variant: "danger" },
  { value: "oko_staff", label: "OKO Staff", variant: "warning" },
  { value: "client_admin", label: "Client Admin", variant: "info" },
  { value: "school_admin", label: "School Admin", variant: "info" },
  { value: "content_manager", label: "Content Manager", variant: "success" },
  { value: "educator", label: "Educator", variant: "primary" },
  { value: "student", label: "Student", variant: "secondary" },
];

const SCHOOL_SCOPED_ROLES = ["school_admin", "educator", "spectator", "student"];
const CLIENT_SCOPED_ROLES = ["client_admin"];

const isSchoolScopedRole = (role) => SCHOOL_SCOPED_ROLES.includes(role);
const isClientScopedRole = (role) => CLIENT_SCOPED_ROLES.includes(role);

export const UserEdit = () => {
  const { id } = useParams();
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [schools, setSchools] = useState([]);
  const [clients, setClients] = useState([]);
  const [adminUserData, setAdminUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [selectedNewRole, setSelectedNewRole] = useState("");
  const [selectedScopeSchool, setSelectedScopeSchool] = useState("");
  const [selectedScopeClient, setSelectedScopeClient] = useState("");
  const fetchedIdRef = React.useRef(null);

  const [formData, setFormData] = useState({
    display_name: "",
    educator_type: "",
    school_id: "",
    record_sessions: false,
  });

  useEffect(() => {
    if (userLoading) return;

    if (!authUserData || authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }

    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [userResponse, schoolsResponse, clientsResponse, adminMeResponse] = await Promise.all([
          request(`admin/users/${id}`, { method: "GET" }),
          request(`admin/schools`, { method: "GET" }),
          request(`admin/clients`, { method: "GET" }),
          request(`admin/me`, { method: "GET" }).catch(() => null),
        ]);
        setUser(userResponse);
        setSchools(schoolsResponse.schools || []);
        setClients(clientsResponse.clients || []);
        setAdminUserData(adminMeResponse);
        setFormData({
          display_name: userResponse.display_name || "",
          educator_type: userResponse.educator_type || "",
          school_id: userResponse.school_id || "",
          record_sessions: userResponse.record_sessions || false,
        });
      } catch (err) {
        console.error("Failed to fetch data:", err);
        if (err.status === 403) {
          setError("You do not have permission to edit this user.");
        } else if (err.status === 404) {
          setError("User not found.");
        } else {
          setError("Failed to load data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, userLoading, history, authUserData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const payload = {
        display_name: formData.display_name || null,
        educator_type: formData.educator_type || null,
        school_id: formData.school_id || null,
        record_sessions: formData.record_sessions,
      };

      const updatedUser = await request(`admin/users/${id}`, {
        method: "PUT",
        payload,
      });

      setUser(prev => ({
        ...prev,
        ...updatedUser,
        roles: updatedUser.roles ?? prev?.roles ?? [],
        role_assignments:
          updatedUser.role_assignments ?? prev?.role_assignments ?? [],
      }));
      setSuccess(true);
    } catch (err) {
      console.error("Failed to update user:", err);
      if (err.status === 403) {
        setError("You do not have permission to update this user.");
      } else {
        setError("Failed to update user. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedNewRole) return;

    const needsSchool = isSchoolScopedRole(selectedNewRole);
    const needsClient = isClientScopedRole(selectedNewRole);

    if (needsSchool && !selectedScopeSchool) {
      setRoleError("Select a school for this role.");
      return;
    }
    if (needsClient && !selectedScopeClient) {
      setRoleError("Select a client for this role.");
      return;
    }

    setSavingRole(true);
    setRoleError(null);

    const payload = {
      role: selectedNewRole,
      school_id: needsSchool ? selectedScopeSchool : null,
      client_id: needsClient ? selectedScopeClient : null,
    };

    try {
      const created = await request(`admin/users/${id}/roles`, {
        method: "POST",
        payload,
      });

      setUser(prev => ({
        ...prev,
        roles: (prev.roles || []).includes(selectedNewRole)
          ? prev.roles
          : [...(prev.roles || []), selectedNewRole],
        role_assignments: [...(prev.role_assignments || []), created],
      }));
      setSelectedNewRole("");
      setSelectedScopeSchool("");
      setSelectedScopeClient("");
    } catch (err) {
      console.error("Failed to add role:", err);
      if (err.status === 409) {
        setRoleError("User already has this role for the selected scope.");
      } else if (err.status === 422) {
        setRoleError("Invalid scope for this role.");
      } else {
        setRoleError("Failed to add role. Please try again.");
      }
    } finally {
      setSavingRole(false);
    }
  };

  const handleRemoveRole = async (assignment) => {
    const { role, client_id, school_id } = assignment;
    if (!window.confirm(`Remove ${role} role from this user?`)) return;

    setSavingRole(true);
    setRoleError(null);

    const params = new URLSearchParams();
    if (client_id) params.append("client_id", client_id);
    if (school_id) params.append("school_id", school_id);
    const query = params.toString() ? `?${params.toString()}` : "";

    try {
      await request(`admin/users/${id}/roles/${role}${query}`, {
        method: "DELETE",
      });

      setUser(prev => {
        const remaining = (prev.role_assignments || []).filter(
          a =>
            !(
              a.role === role &&
              (a.client_id || null) === (client_id || null) &&
              (a.school_id || null) === (school_id || null)
            )
        );
        const stillHasRole = remaining.some(a => a.role === role);
        return {
          ...prev,
          role_assignments: remaining,
          roles: stillHasRole
            ? prev.roles
            : (prev.roles || []).filter(r => r !== role),
        };
      });
    } catch (err) {
      console.error("Failed to remove role:", err);
      setRoleError("Failed to remove role. Please try again.");
    } finally {
      setSavingRole(false);
    }
  };

  const getRoleVariant = (role) => {
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.variant : "secondary";
  };

  const getRoleLabel = (role) => {
    const found = ROLE_OPTIONS.find(r => r.value === role);
    return found ? found.label : role;
  };

  const getSchoolName = (schoolId) => {
    const found = schools.find(s => s.id === schoolId);
    return found ? found.name : schoolId;
  };

  const getClientName = (clientId) => {
    const found = clients.find(c => c.id === clientId);
    return found ? found.name : clientId;
  };

  const getScopeLabel = (assignment) => {
    if (assignment.school_id) return `@ ${getSchoolName(assignment.school_id)}`;
    if (assignment.client_id) return `@ ${getClientName(assignment.client_id)}`;
    return "";
  };

  // Scoped roles (school/client) can be assigned multiple times under
  // different scopes, so only hide global roles the user already has.
  const availableRolesToAdd = ROLE_OPTIONS.filter(r => {
    if (isSchoolScopedRole(r.value) || isClientScopedRole(r.value)) return true;
    return !(user?.roles || []).includes(r.value);
  });

  const roleAssignments = user?.role_assignments || [];

  const hasCoTeacherLink = Boolean(user?.co_teacher_of) || (user?.co_teachers || []).length > 0;
  const schoolChangeRemovesLinks =
    hasCoTeacherLink && formData.school_id !== (user?.school_id || "");

  if (userLoading || loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  if (error && !user) {
    return (
      <PageContainer>
        <BackLink to={generateRoutePath(constants.USER_ADMIN_ROUTE_NAME)}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Users
        </BackLink>
        <Alert variant="danger">{error}</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackLink to={generateRoutePath(constants.USER_ADMIN_DETAIL_ROUTE_NAME, { id })}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to User
      </BackLink>

      <PageHeader>
        <PageTitle>Edit User</PageTitle>
      </PageHeader>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" className="mb-3">User updated successfully!</Alert>}

      <StyledCard>
        <CardHeader>Basic Information</CardHeader>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Display Name</Form.Label>
              <Form.Control
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                placeholder="Enter display name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="text"
                value={user?.email || "-"}
                disabled
              />
              <Form.Text className="text-muted">
                Email is managed by Firebase and cannot be changed here
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Educator Type</Form.Label>
              <Form.Select
                name="educator_type"
                value={formData.educator_type}
                onChange={handleChange}
              >
                {EDUCATOR_TYPE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>School</Form.Label>
              <Form.Select
                name="school_id"
                value={formData.school_id}
                onChange={handleChange}
              >
                <option value="">No School</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </Form.Select>
              {schoolChangeRemovesLinks && (
                <Form.Text className="text-danger">
                  Changing the school removes this teacher's co-teacher links.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="record-sessions-switch"
                name="record_sessions"
                label="Record Sessions"
                checked={formData.record_sessions}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                Enable recording of user sessions for review and analysis
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={() => history.push(generateRoutePath(constants.USER_ADMIN_DETAIL_ROUTE_NAME, { id }))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </StyledCard>

      <StyledCard>
        <CardHeader>Roles</CardHeader>
        <Card.Body>
          {roleError && <Alert variant="danger" className="mb-3">{roleError}</Alert>}

          <Form.Label>Current Roles</Form.Label>
          <RolesContainer>
            {roleAssignments.length > 0 ? (
              roleAssignments.map(assignment => (
                <RoleBadge
                  key={assignment.id}
                  bg={getRoleVariant(assignment.role)}
                  onClick={() => handleRemoveRole(assignment)}
                  title="Click to remove"
                >
                  {getRoleLabel(assignment.role)} {getScopeLabel(assignment)}
                  <FontAwesomeIcon icon={faTimes} className="ms-2" />
                </RoleBadge>
              ))
            ) : (
              <span className="text-muted">No roles assigned</span>
            )}
          </RolesContainer>

          {availableRolesToAdd.length > 0 && (
            <>
              <Form.Label>Add Role</Form.Label>
              <AddRoleContainer>
                <Form.Select
                  value={selectedNewRole}
                  onChange={(e) => {
                    setSelectedNewRole(e.target.value);
                    setSelectedScopeSchool("");
                    setSelectedScopeClient("");
                    setRoleError(null);
                  }}
                  style={{ maxWidth: "200px" }}
                >
                  <option value="">Select role...</option>
                  {availableRolesToAdd.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                {isSchoolScopedRole(selectedNewRole) && (
                  <Form.Select
                    value={selectedScopeSchool}
                    onChange={(e) => setSelectedScopeSchool(e.target.value)}
                    style={{ maxWidth: "240px" }}
                  >
                    <option value="">Select school...</option>
                    {schools.map(school => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {isClientScopedRole(selectedNewRole) && (
                  <Form.Select
                    value={selectedScopeClient}
                    onChange={(e) => setSelectedScopeClient(e.target.value)}
                    style={{ maxWidth: "240px" }}
                  >
                    <option value="">Select client...</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  variant="outline-primary"
                  onClick={handleAddRole}
                  disabled={!selectedNewRole || savingRole}
                >
                  {savingRole ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add
                    </>
                  )}
                </Button>
              </AddRoleContainer>
            </>
          )}
        </Card.Body>
      </StyledCard>

      <CoTeacherCard
        user={user}
        variant={EDIT_PAGE}
        editable={Boolean(adminUserData?.can_access_user_admin)}
        onUserChange={(changes) => setUser(prev => ({ ...prev, ...changes }))}
      />
    </PageContainer>
  );
};
